import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Package, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminOrderDistribution() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState({}); // key = `${orderId}_${productId}_${farmerId}`, value = kg

  const load = () => api.get('/admin/order-distribution/summary').then(r => { setData(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setAssign = (key, kg) => setAssignments({ ...assignments, [key]: kg });

  const confirmForProduct = async (productAgg) => {
    const entries = [];
    for (const o of productAgg.orders) {
      for (const f of productAgg.available_farmers) {
        const key = `${o.order_id}_${productAgg.product_id}_${f.farmer_id}`;
        const kg = parseFloat(assignments[key] || 0);
        if (kg > 0) {
          entries.push({
            order_id: o.order_id,
            product_id: productAgg.product_id,
            farmer_id: f.farmer_id,
            assigned_kg: kg,
            procurement_price: parseFloat(f.procurement_price) || 0,
          });
        }
      }
    }
    if (entries.length === 0) { toast.error('Enter at least one assignment'); return; }
    try {
      for (const e of entries) {
        await api.post('/admin/order-distribution/assign', e);
      }
      toast.success(`${entries.length} assignment(s) confirmed`);
      setAssignments({});
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
  };

  if (loading) return <div className="p-20 flex justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-order-distribution-page" className="p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-serif font-medium">Order Distribution</h1>
        <p className="text-sm text-muted-foreground">Assign customer orders to farmers. Each unit of KG counts.</p>
      </div>

      {data.length === 0 ? (
        <Card className="rounded-xl"><CardContent className="py-14 text-center text-muted-foreground text-sm">All orders distributed. No pending assignments.</CardContent></Card>
      ) : (
        <div className="space-y-6">
          {data.map((p) => {
            const orderSum = assignments ? Object.entries(assignments).filter(([k]) => k.includes(p.product_id)).reduce((acc, [, v]) => acc + (parseFloat(v) || 0), 0) : 0;
            const over = orderSum > p.remaining;
            return (
              <Card key={p.product_id} data-testid={`dist-product-${p.product_id}`} className="rounded-xl">
                <CardHeader className="flex-row items-center gap-4">
                  {p.product_image && <img src={p.product_image} alt="" className="w-14 h-14 rounded-xl object-cover" />}
                  <div className="flex-1">
                    <CardTitle className="text-base">{p.product_name}</CardTitle>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>Ordered: <strong className="text-foreground">{p.total_ordered} kg</strong></span>
                      <span>Assigned: <strong className="text-foreground">{p.total_assigned} kg</strong></span>
                      <Badge className="rounded-full bg-amber-100 text-amber-800 border-0">Remaining: {p.remaining} kg</Badge>
                    </div>
                  </div>
                  <Button data-testid={`confirm-dist-${p.product_id}`} onClick={() => confirmForProduct(p)} disabled={orderSum === 0 || over} className="rounded-full">
                    Confirm Distribution
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  {over && (
                    <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-xs flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Assigned KG exceeds remaining demand by {(orderSum - p.remaining).toFixed(1)}kg
                    </div>
                  )}
                  {p.available_farmers.length === 0 ? (
                    <div className="p-3 rounded-xl bg-amber-50 text-amber-900 text-xs flex items-center gap-2">
                      <Users className="h-4 w-4" /> No active farmers linked to this product. Link farmers in the product edit page first.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {p.orders.map((o) => (
                        <div key={o.order_id} className="border rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-sm font-medium">{o.order_id}</p>
                              <p className="text-xs text-muted-foreground">{o.customer} · {o.phone} · Needs {o.qty} kg</p>
                            </div>
                            <Badge variant="outline" className="rounded-full text-[10px] capitalize">{o.status?.replace(/_/g, ' ')}</Badge>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                            {p.available_farmers.map((f) => {
                              const key = `${o.order_id}_${p.product_id}_${f.farmer_id}`;
                              return (
                                <div key={key} data-testid={`assign-cell-${o.order_id}-${f.farmer_id}`} className="flex items-center gap-2 bg-secondary/30 rounded-lg p-2">
                                  <img src={f.farmer?.profile_image} alt="" className="w-8 h-8 rounded-full object-cover" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{f.farmer?.name}</p>
                                    <p className="text-[10px] text-muted-foreground">{'\u20B9'}{f.procurement_price}/kg · {f.approximate_stock_kg}kg avail</p>
                                  </div>
                                  <Input
                                    data-testid={`assign-input-${o.order_id}-${f.farmer_id}`}
                                    type="number" min="0" max={Math.min(o.qty, f.approximate_stock_kg)}
                                    value={assignments[key] || ''}
                                    onChange={e => setAssign(key, e.target.value)}
                                    placeholder="kg" className="w-16 h-8 text-xs"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
